/* =============================================================
   SKYFALL — Globe (production)
   Wireframe earth + continents + HQ pins + 7 agent pins + abstract satellite
   Auto-rotate only (no drag). Respects prefers-reduced-motion. Loads as ESM.
   ============================================================= */
(async function () {
  const mount = document.querySelector('[data-sk-globe]');
  if (!mount) return;

  // Defer ALL heavy work (three.js download, parse, scene build) until the
  // page has finished loading and the main thread is idle — the globe is hero
  // decoration and must never compete with LCP/TBT on a throttled device.
  if (document.readyState !== 'complete') {
    await new Promise((r) => window.addEventListener('load', r, { once: true }));
  }
  await new Promise((r) => (
    'requestIdleCallback' in window ? requestIdleCallback(r, { timeout: 2500 }) : setTimeout(r, 400)
  ));

  // Load the heavy 3D deps only on pages that actually render a globe.
  // (Static top-level imports would download three.js on every page.)
  const THREE = await import('three');
  const topojson = await import('topojson-client');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Scene / renderer -----------------------------------------
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  camera.position.set(0, 0, 5);
  // Aim camera slightly above the scene origin so the globe visually sits lower
  // in the frame. No CSS clipping; the globe is always fully rendered.
  camera.lookAt(0, 0.83, 0);

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch (e) {
    mount.classList.add('sk-globe--fallback');
    return; // WebGL unavailable — leave the CSS fallback
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);
  mount.appendChild(renderer.domElement);

  function resize() {
    const w = mount.clientWidth;
    const h = mount.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(mount);
  resize();

  // --- Group (whole earth spins as a unit) --
  const group = new THREE.Group();
  group.position.set(0, 0, 0);
  scene.add(group);

  const GLOBE_R = 1.35;

  // --- Wireframe sphere -----------------------------------------
  const sphereGeo = new THREE.SphereGeometry(GLOBE_R, 22, 22);
  const wireMat = new THREE.LineBasicMaterial({
    color: 0xc9ccd1, transparent: true, opacity: 0.18,
  });
  const globeMesh = new THREE.LineSegments(new THREE.WireframeGeometry(sphereGeo), wireMat);
  group.add(globeMesh);

  // Outer halo
  const haloMat = new THREE.MeshBasicMaterial({
    color: 0xf6db80, transparent: true, opacity: 0.04, side: THREE.BackSide,
  });
  group.add(new THREE.Mesh(new THREE.SphereGeometry(GLOBE_R * 1.04, 32, 32), haloMat));

  // --- Orbital rings --------------------------------------------
  const ringConfigs = [
    { tilt: 0.4,  radius: GLOBE_R * 1.25, opacity: 0.25 },
    { tilt: -0.3, radius: GLOBE_R * 1.40, opacity: 0.18 },
    { tilt: 1.3,  radius: GLOBE_R * 1.55, opacity: 0.12 },
  ];
  ringConfigs.forEach((cfg) => {
    const pts = [];
    const segs = 96;
    for (let i = 0; i <= segs; i++) {
      const a = (i / segs) * Math.PI * 2;
      pts.push(new THREE.Vector3(Math.cos(a) * cfg.radius, 0, Math.sin(a) * cfg.radius));
    }
    const ring = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({ color: 0xf6db80, transparent: true, opacity: cfg.opacity })
    );
    ring.rotation.x = cfg.tilt;
    ring.rotation.z = cfg.tilt * 0.3;
    group.add(ring);
  });

  // --- lat/lon → vec3 helper ------------------------------------
  function latLonToVec3(lat, lon, r) {
    const phi = (90 - lat) * Math.PI / 180;
    const theta = (lon + 180) * Math.PI / 180;
    return new THREE.Vector3(
      -r * Math.sin(phi) * Math.cos(theta),
       r * Math.cos(phi),
       r * Math.sin(phi) * Math.sin(theta),
    );
  }

  // --- Continent outlines (lazy-loaded topojson) ----------------
  async function loadContinents() {
    try {
      const landScript = document.querySelector('script[data-land-url]');
      const landUrl = (landScript && landScript.dataset.landUrl) || 'https://cdn.jsdelivr.net/npm/world-atlas@2/land-110m.json';
      const res = await fetch(landUrl);
      const topology = await res.json();
      const land = topojson.feature(topology, topology.objects.land);
      const mat = new THREE.LineBasicMaterial({ color: 0xecedee, transparent: true, opacity: 0.7 });
      const drawRing = (ring) => {
        const pts = ring.map(([lon, lat]) => latLonToVec3(lat, lon, GLOBE_R * 1.003));
        group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat));
      };
      land.features.forEach((f) => {
        const g = f.geometry;
        if (g.type === 'Polygon') g.coordinates.forEach(drawRing);
        else if (g.type === 'MultiPolygon') g.coordinates.forEach((p) => p.forEach(drawRing));
      });
    } catch (err) { /* silent */ }
  }
  loadContinents();

  // --- Tag registry (HTML labels that follow 3D pin positions) --
  const tagsContainer = document.querySelector('[data-sk-globe-tags]');
  const pinTags = [];
  function addTag({ position, kind, id, label }) {
    if (!tagsContainer) return;
    const anchor = new THREE.Object3D();
    anchor.position.copy(position.clone().multiplyScalar(1.08));
    group.add(anchor);
    const el = document.createElement('div');
    el.className = 'sk-globe__tag' + (kind === 'hq' ? ' sk-globe__tag--hq' : '');
    el.innerHTML = '<span class="sk-globe__tag-id">' + id + '</span>' + label;
    tagsContainer.appendChild(el);
    pinTags.push({ el, anchor });
  }

  // --- HQs (Ottawa + West Palm Beach) -------------------------
  const hqStations = [
    { name: 'OTT', label: 'Ottawa',          lat: 45.4215, lon: -75.6972 },
    { name: 'WPB', label: 'West Palm Beach', lat: 26.7056, lon: -80.0364 },
  ];
  hqStations.forEach((s) => {
    const pos = latLonToVec3(s.lat, s.lon, GLOBE_R);
    // The surface dot mesh has always been stuck at (0,0,0) because
    // `group.add(mesh).position.copy(pos)` sets the GROUP's position, not the
    // mesh's. Keep the side-effect so the globe stays where it sits today,
    // but render the dot invisible so it no longer shows in the center.
    group.add(new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 24, 24),
      new THREE.MeshBasicMaterial({ color: 0xffe8a3, transparent: true, opacity: 0 })
    )).position.copy(pos);

    const haloMat2 = new THREE.MeshBasicMaterial({
      color: 0xf6db80, side: THREE.DoubleSide, transparent: true, opacity: 0.75,
    });
    const halo1 = new THREE.Mesh(new THREE.RingGeometry(0.08, 0.11, 32), haloMat2);
    halo1.position.copy(pos);
    halo1.lookAt(pos.clone().multiplyScalar(2));
    group.add(halo1);
    const halo2 = new THREE.Mesh(new THREE.RingGeometry(0.055, 0.065, 32), haloMat2.clone());
    halo2.material.opacity = 0.55;
    halo2.position.copy(pos);
    halo2.lookAt(pos.clone().multiplyScalar(2));
    group.add(halo2);

    const beam = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([pos.clone(), pos.clone().multiplyScalar(1.22)]),
      new THREE.LineBasicMaterial({ color: 0xf6db80, transparent: true, opacity: 0.95 })
    );
    group.add(beam);

    addTag({ position: pos, kind: 'hq', id: 'HQ', label: s.label });
  });

  // --- 7 field operatives (one per continent) -------------------
  const operatives = [
    { id: 'AGENT 01', label: 'Los Angeles', lat: 34.0522,  lon: -118.2437 },
    { id: 'AGENT 02', label: 'São Paulo',   lat: -23.5505, lon: -46.6333  },
    { id: 'AGENT 03', label: 'London',      lat: 51.5074,  lon: -0.1278   },
    { id: 'AGENT 04', label: 'Cape Town',   lat: -33.9249, lon: 18.4241   },
    { id: 'AGENT 05', label: 'Tokyo',       lat: 35.6762,  lon: 139.6503  },
    { id: 'AGENT 06', label: 'Sydney',      lat: -33.8688, lon: 151.2093  },
    { id: 'AGENT 07', label: 'McMurdo',     lat: -77.8500, lon: 166.6667  },
  ];
  operatives.forEach((op) => {
    const pos = latLonToVec3(op.lat, op.lon, GLOBE_R);
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(0.022, 14, 14),
      new THREE.MeshBasicMaterial({ color: 0xc9ccd1 })
    );
    dot.position.copy(pos);
    group.add(dot);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.035, 0.042, 20),
      new THREE.MeshBasicMaterial({ color: 0xc9ccd1, side: THREE.DoubleSide, transparent: true, opacity: 0.55 })
    );
    ring.position.copy(pos);
    ring.lookAt(pos.clone().multiplyScalar(2));
    group.add(ring);

    const stem = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([pos.clone(), pos.clone().multiplyScalar(1.08)]),
      new THREE.LineBasicMaterial({ color: 0xc9ccd1, transparent: true, opacity: 0.65 })
    );
    group.add(stem);

    addTag({ position: pos, kind: 'op', id: op.id, label: op.label });
  });

  // --- Abstract satellite (orbiter) -----------------------------
  const satCfg = {
    radius: GLOBE_R * 1.38,
    tilt: 0.25,
    twist: 0.12,
    period: 9000,
    phase: 0,
    trail: 36,
  };
  const satEuler = new THREE.Euler(satCfg.tilt, 0, satCfg.twist, 'XYZ');
  const S = 0.6;
  const satellite = new THREE.Group();
  const coreSize = 0.045 * S;
  const coreGeo = new THREE.OctahedronGeometry(coreSize);
  satellite.add(new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({ color: 0xc9ccd1 })));
  satellite.add(new THREE.LineSegments(
    new THREE.EdgesGeometry(coreGeo),
    new THREE.LineBasicMaterial({ color: 0xf6db80, transparent: true, opacity: 0.95 })
  ));
  const panelW = 0.14 * S, panelH = 0.06 * S;
  function wirePanel(xOffset) {
    const pts = [
      new THREE.Vector3(-panelW / 2, 0, -panelH / 2),
      new THREE.Vector3( panelW / 2, 0, -panelH / 2),
      new THREE.Vector3( panelW / 2, 0,  panelH / 2),
      new THREE.Vector3(-panelW / 2, 0,  panelH / 2),
      new THREE.Vector3(-panelW / 2, 0, -panelH / 2),
    ];
    const outline = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({ color: 0xc9ccd1, transparent: true, opacity: 0.75 })
    );
    outline.position.x = xOffset;
    satellite.add(outline);
    const cross = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-panelW / 2, 0, -panelH / 2),
        new THREE.Vector3( panelW / 2, 0,  panelH / 2),
      ]),
      new THREE.LineBasicMaterial({ color: 0x6a6f77, transparent: true, opacity: 0.6 })
    );
    cross.position.x = xOffset;
    satellite.add(cross);
    const strut = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(xOffset > 0 ? xOffset - panelW / 2 : xOffset + panelW / 2, 0, 0),
        new THREE.Vector3(Math.sign(xOffset) * coreSize * 0.7, 0, 0),
      ]),
      new THREE.LineBasicMaterial({ color: 0xc9ccd1, transparent: true, opacity: 0.75 })
    );
    satellite.add(strut);
  }
  wirePanel( 0.12 * S);
  wirePanel(-0.12 * S);
  satellite.add(new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0.09 * S, 0),
    ]),
    new THREE.LineBasicMaterial({ color: 0xf6db80, transparent: true, opacity: 0.9 })
  ));
  const antennaTip = new THREE.Mesh(
    new THREE.SphereGeometry(0.0055 * S, 8, 8),
    new THREE.MeshBasicMaterial({ color: 0xf6db80 })
  );
  antennaTip.position.y = 0.09 * S;
  satellite.add(antennaTip);
  group.add(satellite);

  const trailPositions = new Float32Array(satCfg.trail * 3);
  const trailGeom = new THREE.BufferGeometry();
  trailGeom.setAttribute('position', new THREE.BufferAttribute(trailPositions, 3));
  const trailLine = new THREE.Line(trailGeom, new THREE.LineBasicMaterial({
    color: 0xf6db80, transparent: true, opacity: 0.55,
  }));
  group.add(trailLine);
  const satTrail = [];

  // --- Projection helpers (3D → HTML tag positions) -------------
  const tmpVec = new THREE.Vector3();
  const tmpDir = new THREE.Vector3();
  const tmpCam = new THREE.Vector3();
  function updateTags() {
    if (!pinTags.length) return;
    const rect = mount.getBoundingClientRect();
    pinTags.forEach((t) => {
      t.anchor.getWorldPosition(tmpVec);
      tmpDir.copy(tmpVec).sub(group.position).normalize();
      tmpCam.copy(camera.position).sub(group.position).normalize();
      const dot = tmpDir.dot(tmpCam);
      const isFront = dot > 0.15;
      const projected = tmpVec.clone().project(camera);
      const x = (projected.x * 0.5 + 0.5) * rect.width;
      const y = (projected.y * -0.5 + 0.5) * rect.height;
      t.el.style.transform = 'translate(calc(-50% + ' + x.toFixed(1) + 'px), calc(-50% + ' + y.toFixed(1) + 'px))';
      t.el.style.left = '0';
      t.el.style.top = '0';
      if (isFront) {
        t.el.classList.add('is-visible');
        const limb = (dot - 0.15) / 0.85;
        t.el.style.opacity = Math.max(0.2, Math.min(1, limb * 1.4));
      } else {
        t.el.classList.remove('is-visible');
        t.el.style.opacity = 0;
      }
    });
  }

  // --- Animation loop -------------------------------------------
  const ROT_SPEED = reduceMotion ? 0 : 0.16;

  // --- Interactive drag rotation ---------------------------------
  let isDragging = false;
  let lastPointerX = 0;
  let lastPointerY = 0;
  const canvas = renderer.domElement;
  canvas.style.cursor = 'grab';
  canvas.style.touchAction = 'none'; // prevent touch scrolling while dragging

  canvas.addEventListener('pointerdown', (e) => {
    isDragging = true;
    lastPointerX = e.clientX;
    lastPointerY = e.clientY;
    canvas.style.cursor = 'grabbing';
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!isDragging) return;
    const dx = e.clientX - lastPointerX;
    const dy = e.clientY - lastPointerY;
    lastPointerX = e.clientX;
    lastPointerY = e.clientY;
    // Yaw (horizontal drag → rotate around Y axis)
    group.rotation.y += dx * 0.006;
    // Pitch (vertical drag → rotate around X axis) — clamped to ±75°
    group.rotation.x = Math.max(
      -Math.PI * 0.42,
      Math.min(Math.PI * 0.42, group.rotation.x + dy * 0.006)
    );
  });
  function endDrag(e) {
    if (!isDragging) return;
    isDragging = false;
    canvas.style.cursor = 'grab';
    try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
  }
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);
  canvas.addEventListener('pointerleave', endDrag);
  const APPLIED_SCALE = 0.8625;
  let t0 = performance.now();
  let rafId = null;
  let posterCleared = false;      // static poster shows until the first live frame paints
  let inView = true;              // globe is within the viewport
  let pageVisible = !document.hidden;

  function startLoop() {
    if (rafId !== null || !inView || !pageVisible) return;
    t0 = performance.now();       // reset clock so dt doesn't jump after a pause
    rafId = requestAnimationFrame(tick);
  }
  function stopLoop() {
    if (rafId === null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
  }

  function tick(now) {
    const dt = (now - t0) / 1000;
    t0 = now;

    // Auto-rotate only when not being dragged (smoothly slowed if dragging)
    group.rotation.y += ROT_SPEED * dt * (isDragging ? 0.05 : 1);
    group.scale.setScalar(APPLIED_SCALE);

    // Satellite
    const a = (now / satCfg.period + satCfg.phase) % (Math.PI * 2);
    const sx = Math.cos(a) * satCfg.radius;
    const sz = Math.sin(a) * satCfg.radius;
    const satLocal = new THREE.Vector3(sx, 0, sz).applyEuler(satEuler);
    satellite.position.copy(satLocal);
    const tangent = new THREE.Vector3(-Math.sin(a) * satCfg.radius, 0, Math.cos(a) * satCfg.radius).applyEuler(satEuler);
    satellite.lookAt(satellite.position.clone().add(tangent));
    satellite.rotation.z += 0.008;

    // Trail
    satTrail.unshift(satLocal);
    if (satTrail.length > satCfg.trail) satTrail.pop();
    for (let i = 0; i < satCfg.trail; i++) {
      const p = satTrail[i] || satLocal;
      trailPositions[i * 3]     = p.x;
      trailPositions[i * 3 + 1] = p.y;
      trailPositions[i * 3 + 2] = p.z;
    }
    trailGeom.attributes.position.needsUpdate = true;

    updateTags();
    renderer.render(scene, camera);
    // First real frame is on screen — fade out the static poster.
    if (!posterCleared) { posterCleared = true; mount.classList.add('sk-globe--live'); }
    rafId = requestAnimationFrame(tick);
  }

  // Render only while the globe is actually on-screen AND the tab is visible.
  // A perpetual WebGL loop is the single biggest source of background churn;
  // gating it halts all GPU/main-thread work the moment it scrolls out of view.
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      inView = entries[0].isIntersecting;
      if (inView) startLoop(); else stopLoop();
    }, { threshold: 0 }).observe(mount);
  }
  document.addEventListener('visibilitychange', () => {
    pageVisible = !document.hidden;
    if (pageVisible) startLoop(); else stopLoop();
  });
  startLoop();

  // --- Morse blink (S · K · Y loop) -----------------------------
  const morseDot = document.querySelector('[data-sk-globe-morse]');
  if (morseDot && !reduceMotion) {
    const MORSE_UNIT = 180;
    const seq = [
      ['on', 1], ['off', 1], ['on', 1], ['off', 1], ['on', 1],
      ['off', 3],
      ['on', 3], ['off', 1], ['on', 1], ['off', 1], ['on', 3],
      ['off', 3],
      ['on', 3], ['off', 1], ['on', 1], ['off', 1], ['on', 3], ['off', 1], ['on', 3],
      ['off', 7],
    ];
    function playMorse() {
      let t = 0;
      seq.forEach(([state, units]) => {
        setTimeout(() => morseDot.classList.toggle('is-on', state === 'on'), t);
        t += units * MORSE_UNIT;
      });
      setTimeout(playMorse, t);
    }
    playMorse();
  }

  // --- Mission clock + telemetry --------------------------------
  const clockEl = document.querySelector('[data-sk-globe-clock]');
  const signalEl = document.querySelector('[data-sk-globe-signal]');
  const missionStart = performance.now();
  function updateTelemetry() {
    const secs = Math.floor((performance.now() - missionStart) / 1000);
    const h = String(Math.floor(secs / 3600)).padStart(2, '0');
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    if (clockEl) clockEl.textContent = h + ':' + m + ':' + s;
    if (signalEl) {
      const strength = 90 + Math.floor(Math.sin(secs * 0.3) * 5) + Math.floor(Math.random() * 4);
      signalEl.textContent = 'STRONG · ' + strength + '%';
    }
  }
  if (clockEl || signalEl) {
    updateTelemetry();
    setInterval(updateTelemetry, 500);
  }
})();
